import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import useEditDemand from "../graphql/useEditDemand";
import cx from "classnames";
import { IForecastContextInterface } from "../../../../../context";
import Table, { CheckboxCell, InputCell } from "../../../../../../../common/components/Table";
import Paginator from "../../../../../../../common/components/Paginator/Paginator";
import Icon from "../../../../../../../common/components/Icon/Icon";
import { DemandAdjustment } from "../../../../../../../models/ISchema";
import { useStringFilterParams } from "../../dashboard/utils";
import LoaderTable from "../../../../../../../common/components/LoaderTable/LoaderTable";
import SearchInput from "../../../../../../../common/components/SearchInput/SearchInput";
import css from "./subjectList.module.scss";

interface ICurriculumProps {
  project: any;
  isLoading: boolean;
  searchTerm: string;
  forecastContext: IForecastContextInterface;
  setDemandFilter: (page: number, searchTerm: string) => void;
}
interface IPageInfo {
  total: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  page: number;
  size: number;
}

const SubjectList: React.FC<ICurriculumProps> = (props: ICurriculumProps) => {
  const { project, setDemandFilter, isLoading, searchTerm } = props;
  const context: IForecastContextInterface = props.forecastContext;
  const [data, setData] = useState(project.unit.demandsPage.items);
  const [isLoadingFilter, setIsLoadingFilter] = useState(false);
  const [isLoadingPaginator, setIsLoadingPaginator] = useState(false);
  const [lastRowUpdatedId, setLastRowUpdatedId] = useState<number>(null);
  const pageInfo: IPageInfo = project.unit.demandsPage.pageInfo;
  const [loading, setLoading] = useState(false);
  const editDemand = useEditDemand();
  const routeBack = context.routes.dashboard();
  const stringFilter = useStringFilterParams();
  let countDemandOwn = 0;
  const selectedRow = data.map(value => {
    if (value.own) countDemandOwn++;
    return value.checked;
  });

  useEffect(() => {
    setData(project.unit.demandsPage.items);
  }, [project]);

  useEffect(() => {
    if (!isLoading) {
      setIsLoadingPaginator(false);
      setIsLoadingFilter(false);
    }
  }, [isLoading]);

  const handleSaveDemand = async () => {
    try {
      setLoading(true);
      const unitId = project.unit.id;
      const demandAdjustments: DemandAdjustment[] = data.map(demand => {
        return {
          demandId: demand.id,
          adjusted: demand.adjusted,
          confirmed: demand.checked,
        };
      });
      const variables = {
        unitId,
        demandAdjustments,
      };
      if (demandAdjustments.length > 0) {
        await editDemand.editDemand(variables, context);
      }
      setLoading(false);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    handleSaveDemand();
  }, [data]);

  const curriculumCell = (data: any) => {
    const demand: any = data.row.original;
    const levelId = demand?.relatedUnits?.level?.id;
    const levelRoute = levelId ?? context?.routes.unit(levelId);
    return levelId ? (
      <a href={`${levelRoute}${stringFilter}`}>{data.value}</a>
    ) : (
      `${data.value || ""}`
    );
  };

  const courseCell = (data: any) => {
    const demand: any = data.row.original;
    const courseId = demand?.relatedUnits?.subject?.id;
    const courseRoute = courseId ?? context?.routes.unit(courseId);
    return courseId ? (
      <a href={`${courseRoute}${stringFilter}`}>{data.value}</a>
    ) : (
      `${data.value || ""}`
    );
  };

  const adjustedCell = (data: any) => {
    const demand: any = data.row.original;
    if (!demand?.own) return `${data.value}`;
    return (
      <InputCell
        disabled={data.state.selectedRowIds[data.row.index]}
        value={data.value}
        onChange={(value: string) => {
          const inputValue = value ? parseInt(value) : 0;
          data.updateImplementedData(setData, data.row.index, "adjusted", inputValue);
        }}
      />
    );
  };

  const crosslistCell = (data: any) => {
    const demand: any = data.row.original;
    const crossListId = demand?.relatedUnits?.crosslist?.id;
    const crossListRoute = crossListId ?? context?.routes.unit(crossListId);
    return crossListId ? (
      <a href={`${crossListRoute}${stringFilter}`}>{data.value}</a>
    ) : (
      `${data.value || ""}`
    );
  };

  const headerCheckedCell = (data: any) => {
    const handleChange = () => {
      const { toggleAllRowsSelected, updateImplementAllData, column, isAllRowsSelected } = data;
      const newChecked = !isAllRowsSelected;
      toggleAllRowsSelected(true);
      updateImplementAllData(setData, column.id, newChecked);
      setLastRowUpdatedId(null);
    };

    return (
      <div className={cx("container-row", "row_align--center")}>
        <CheckboxCell
          {...data.getToggleAllRowsSelectedProps({ onChange: handleChange })}
          disabled={countDemandOwn === 0 || loading}
        />
        <label className={css.labelCheckbox}>Confirmada</label>
      </div>
    );
  };

  const checkedCell = (data: any) => {
    const demand: any = data.row.original;
    const disabled = !demand?.own;
    const { row, column, toggleRowSelected, updateImplementedData } = data;
    const handleChange = () => {
      const value = !row.isSelected;
      toggleRowSelected(row.id, value);
      updateImplementedData(setData, row.index, column.id, value);
      setLastRowUpdatedId(row.id);
    };
    return (
      <div className={cx("container-row", "row_align--center")}>
        <CheckboxCell
          {...data.row.getToggleRowSelectedProps({ onChange: handleChange })}
          disabled={disabled || loading}
        />
        {loading && data.row.id === lastRowUpdatedId && (
          <label className={css.labelCheckbox}>Cargando ...</label>
        )}
        {!loading && data.row.id === lastRowUpdatedId && (
          <label className={cx(css.labelCheckbox, css.labelCheckbox_confirm)}>
            <Icon icon="check" className={cx(css.labelCheckbox_confirm_icon)} />
            Guardado
          </label>
        )}
      </div>
    );
  };

  const ruleCodeCell = (data: any) => {
    const demand = data.row.original;
    const { course } = demand;
    const { ruleCode } = course;

    const { relatedUnits } = demand;
    if (relatedUnits?.ruleCode?.id) {
      const ruleCoderoute = context.routes.unit(relatedUnits.ruleCode.id);
      return (
        <a key={relatedUnits.ruleCode.code} href={`${ruleCoderoute}${stringFilter}`}>
          {relatedUnits.ruleCode.code}
        </a>
      );
    } else {
      return "";
    }
  };

  const columns = [
    {
      Header: "Carrera",
      accessor: "course.curriculum.program.name",
      minWidth: 200,
    },
    {
      Header: "Currículo",
      accessor: "course.curriculum.name",
      minWidth: 100,
      Cell: (data: any) => curriculumCell(data),
    },
    {
      Header: "Asignatura",
      accessor: "course.name",
      minWidth: 250,
      Cell: (data: any) => courseCell(data),
    },
    {
      Header: "Lista cruzada",
      accessor: "relatedUnits.crosslist.code",
      Cell: (data: any) => crosslistCell(data),
    },
    {
      Header: "Código Regla",
      accessor: "course.code",
      minWidth: 100,
      Cell: (data: any) => ruleCodeCell(data),
    },
    {
      Header: "Proyectada",
      accessor: "value",
    },
    {
      Header: "Ajustada",
      accessor: "adjusted",
      Cell: (data: any) => adjustedCell(data),
    },
    {
      Header: (data: any) => headerCheckedCell(data),
      accessor: "checked",
      minWidth: 130,
      Cell: (data: any) => checkedCell(data),
    },
  ];

  return (
    <section className={cx(css.cntSubjectList, "container-row")}>
      <Link
        className={cx(css.back, "container-row", "row_align--center")}
        to={`${routeBack}${stringFilter}`}
      >
        <Icon icon="arrow-left" className={cx(css.back_icon)} />
        volver al inicio
      </Link>
      <h1 className={cx(css.cntSubjectList_title, "col_12")}>
        Asignaturas
        <span className={cx(css.detail)}>{` | ${project.unit.label}`}</span>
      </h1>
      <div className="container-row row_aling--center">
        <div className={cx("col_2", css.cntSearchInput)}>
          <SearchInput
            placeholder="Filtrar"
            value={searchTerm}
            onClick={inputValue => {
              setIsLoadingFilter(true);
              setDemandFilter(1, inputValue);
            }}
          />
        </div>
        {isLoadingFilter && (
          <div className={cx("col_1", css.cntSearchInputLoader)}>
            <LoaderTable />
          </div>
        )}
      </div>
      <Table columns={columns} data={data} initSelectedRowIds={selectedRow} />

      <div className={cx(css.cntSubjectList_save, "container-row", "row--center")}>
        <Paginator
          total={pageInfo.total}
          pageSize={pageInfo.size}
          current={pageInfo.page}
          onChange={currentPage => {
            setIsLoadingPaginator(true);
            setDemandFilter(currentPage, "");
          }}
        />
        {isLoadingPaginator && (
          <div className={cx(css.cntPaginatorLoader)}>
            <LoaderTable />
          </div>
        )}
      </div>
    </section>
  );
};

export default SubjectList;
